import * as React from 'react';

import { WrappedMessage } from 'utils';
import messages from '../../displayMessages';
import { showErrorMessage, Icon } from 'ui/components';
import { ChoiceResponse, ViewData, Hints, Submit, SpecificFeedback } from './question-block-shared';
import { XBlocksApi } from 'global/api';
import { RichText } from 'elements';
import { SanitizeConfigOptions } from 'elements/utils/sanitization';

interface Props {
    itemId: string;
    data: ViewData<ChoiceResponse>;
    onUpdateData: (data: ViewData<ChoiceResponse>) => void;
}

interface State {
    submitting: boolean;
}

export class QuestionChoiceResponseBlock extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            submitting: false,
        };
    }

    private async onSubmit() {
        this.setState({ submitting: true });

        const selected = this.props.data.questionData.choices
            .map((x, i) => x.checked ? i : -1)
            .filter((x) => x > -1);

        let data: ViewData<ChoiceResponse>;
        try {
            const response = await XBlocksApi.submitQuestionAnswer({
                id: this.props.itemId,
                data: {
                    selected,
                },
            });
            // parse because GenericObjectSerializer
            data = JSON.parse(response);
        } catch (err) {
            showErrorMessage(
                <WrappedMessage message={messages.errorSubmittingAnswer} />,
                {exception: err}
            );
            this.setState({ submitting: false });
            return;
        }

        this.props.onUpdateData(data);
        this.setState({ submitting: false });
    }

    public render() {
        const data = this.props.data;
        return (
            <div className='question-content'>
                {this.renderQuestion()}
                <Hints hints={data.hints} />
                <Submit
                    hidden={data.correct || (data.maxAttempts > 0 && data.studentAttempts >= data.maxAttempts)}
                    disabled={this.state.submitting}
                    maxAttempts={data.maxAttempts}
                    usedAttempts={data.studentAttempts}
                    onSubmit={() => this.onSubmit()}
                />
            </div>
        );
    }

    private renderQuestion(): React.ReactNode {
        const qData = this.props.data.questionData;
        const id = `${this.props.itemId}-legend`;
        return (
            <fieldset className='question-question' aria-describedby={id}>
                <legend id={id}>
                    <RichText
                       innerHtml={qData.question}
                       sanitizeConfig={SanitizeConfigOptions.UnsafeHTMLAllowedStyles}
                    />
                </legend>
                {this.renderCheckboxes()}
            </fieldset>
        );
    }

    private onOptionSelect(i: number) {
        this.props.onUpdateData({
            ...this.props.data,
            questionData: {
                ...this.props.data.questionData,
                choices: this.props.data.questionData.choices.map((choice, idx) => (
                    {
                        ...choice,
                        checked: idx === i ? !choice.checked : choice.checked,
                    }
                )),
            },
        });
    }

    private renderCheckboxes(): React.ReactNode {
        const data = this.props.data;
        return (
            data.questionData.choices.map((option, i) => {
                const id = `${this.props.itemId}-option-${i}`;
                const isAnswer = (data.questionData.studentAnswer.selected ?? []).includes(i);

                let extraClass;
                let icon;
                if (isAnswer && data.correct === true) {
                    extraClass = 'correct';
                    icon = <Icon fill='white' zoom='28px' name='check' />;
                } else if (isAnswer && data.correct === false) {
                    extraClass = 'incorrect';
                    icon = <Icon fill='white' zoom='28px' name='x' />;
                } else {
                    icon = String.fromCharCode((i % 26) + 65);
                    extraClass = 'unchecked';
                }

                return (
                    <>
                        <div className={`multi field ${extraClass}`}>
                            <input
                                id={id}
                                type='checkbox'
                                value={i}
                                name={`${this.props.itemId}-choices`}
                                checked={option.checked}
                                onChange={() => this.onOptionSelect(i)}
                            />
                            <label htmlFor={id}>
                                <span className='i'>{icon}</span>
                                <span>
                                    <RichText
                                        innerHtml={option.content}
                                        sanitizeConfig={SanitizeConfigOptions.UnsafeHTMLAllowedStyles}
                                    />
                                </span>
                            </label>
                        </div>
                        <SpecificFeedback correct={data.correct} comment={option.comment} />
                    </>
                );
            })
        );
    }
}
